import httpClient from "./HttpClient";
import {
  searchEntraGroups as msalSearchGroups,
  fetchGroupMembersDetailed as msalFetchMembersDetailed,
  fetchGroupInfo as msalFetchGroupInfo,
} from "./msalAuth";

/**
 * Entra security-group reads for the Account Setup access pickers, SERVER-FIRST.
 *
 * The backend resolves groups with an APP-ONLY Graph token (the GroupMember.Read.All application
 * permission consented on the app registration). That is what the admin asked for: hovering a group
 * must not force a Microsoft sign-in, and membership must depend on the app registration rather than
 * on whoever happens to be signed in. The old delegated-token path (msalAuth, this admin's browser)
 * stays as the fallback.
 *
 * WHICH PATH RUNS is decided once per page load, by the first call, and cached in `serverMode`:
 *   - server answers { enabled: true }   -> server from then on. No popup, no consent.
 *   - server answers { enabled: false }  -> GraphGroups:Enabled is off. Fall back to msalAuth, i.e.
 *                                           EXACTLY today's behavior. This is why the feature can ship
 *                                           before the flag is set: nothing changes until it is.
 *   - the request itself fails           -> almost always a backend that predates these routes (404).
 *                                           Fall back too, so a frontend deployed ahead of its backend
 *                                           degrades instead of breaking the picker.
 *
 * ERRORS ARE NOT FALLBACKS. Once the server says enabled, a Graph failure is REPORTED, not quietly
 * retried through MSAL. Falling back there would re-introduce the sign-in popup the admin is trying to
 * get rid of and would disguise a misconfigured app registration as "the feature doesn't work".
 *
 * Every export keeps the exact shape its msalAuth counterpart had, so callers cannot tell which path
 * served them.
 */

// null = not yet determined, true = use the server, false = use delegated MSAL.
let serverMode = null;

// Remembers the LAST group whose info the server bundled with its member list. The members endpoint
// already returns `info` (only when the member list came back empty, which is the sole case the caller
// asks about), so fetchGroupInfo can answer from this instead of paying for a second Graph round trip.
// Single-entry on purpose: the caller always asks members-then-info for the same group, back to back,
// so it cannot grow and cannot drift.
let lastInfo = { id: null, info: null };

function useServer() {
  return serverMode !== false;
}

// A server reply is only authoritative about the mode when it actually parsed as our contract.
// Anything else (network error, 404 from an older backend) permanently selects the MSAL path.
function noteEnabled(data) {
  if (!data || typeof data.enabled !== "boolean") {
    serverMode = false;
    return false;
  }
  serverMode = data.enabled;
  return data.enabled;
}

function fellBack(err) {
  if (serverMode === null || serverMode === true) {
    // Log once, at the moment of the decision, so "why is it still asking me to sign in?" is answerable
    // from the console rather than by guessing.
    console.warn(
      "[Entra] server-side group resolution unavailable — falling back to the delegated browser flow.",
      err && err.message ? err.message : err
    );
  }
  serverMode = false;
}

/** Search the tenant's groups by display-name prefix. -> [{ id, name, mail, kind }] (max 12). */
export async function searchEntraGroups(term) {
  const q = String(term || "").trim();
  if (q.length < 2) return [];

  if (useServer()) {
    try {
      const { data } = await httpClient.get("/report/entra/groups", { params: { term: q } });
      if (noteEnabled(data)) {
        if (data.error) throw new Error(data.error);
        return data.groups || [];
      }
    } catch (e) {
      if (serverMode === true) throw e;   // enabled but failing: report it, don't mask it with a popup
      fellBack(e);
    }
  }
  return msalSearchGroups(q);
}

/**
 * All USER members of a group -> { members: [{ name, email }] sorted by name, scanned }.
 * `scanned` counts every directory object seen, so scanned=0 distinguishes a genuinely empty group
 * from one we were blocked on.
 *
 * THROWS rather than returning an empty list when the read fails. Both the hover card AND the
 * membership sync come through here, and the sync FULL-REPLACES stored membership — so a failure
 * quietly reported as "no members" would revoke real users' access. Callers must see the rejection.
 */
export async function fetchGroupMembersDetailed(groupId) {
  if (useServer()) {
    try {
      const { data } = await httpClient.get("/report/entra/group-members", { params: { id: groupId } });
      if (noteEnabled(data)) {
        if (data.error) throw new Error(data.error);
        lastInfo = { id: groupId, info: data.info || null };
        return { members: data.members || [], scanned: data.scanned || 0 };
      }
    } catch (e) {
      if (serverMode === true) throw e;
      fellBack(e);
    }
  }
  return msalFetchMembersDetailed(groupId);
}

/** Back-compat shape: just the sorted member array. */
export async function fetchGroupMembers(groupId) {
  return (await fetchGroupMembersDetailed(groupId)).members;
}

/** Group metadata, for explaining an empty membership. Best-effort — null when unavailable. */
export async function fetchGroupInfo(groupId) {
  if (serverMode === true) {
    // Served from the members call that immediately preceded this one; no extra Graph round trip.
    return lastInfo.id === groupId ? lastInfo.info : null;
  }
  return msalFetchGroupInfo(groupId);
}

/** Lowercase member emails only — what the access-list sync stores. */
export async function fetchGroupMemberEmails(groupId) {
  return (await fetchGroupMembers(groupId))
    .map((m) => m.email)
    .filter((e) => e && e.indexOf("@") > 0);
}
